import { useEffect, useRef, useState } from "react";
// @ts-ignore -- ren JS sedan tidigare, ingen typning
import { getCurrentlyPlaying } from "../../../Services/SpotifyApi";
// @ts-ignore
import {
  getStoredToken,
  isTokenExpired,
  refreshAccessToken,
  startSpotifyLogin,
} from "../../../Services/SpotifyAuth";
import { http } from "../../../Services/http";
import { listSensors, AdminSensor } from "../../../Services/Admin/Sensors";
import "./SpotifyLabelPanel.css";

/**
 * Märker sensorns ljudklipp med vad Spotify säger spelas.
 *
 * Den här fliken pollar Spotify och postar "nu spelas" till backend. När ett
 * klipp landar slår servern upp klippets STARTTID i den tidslinjen och vet
 * därmed både låt och position, utan att fråga AudD.
 *
 * Fliken måste vara öppen. Det är avsiktligt för det här steget: den
 * återanvänder PKCE-inloggningen som redan finns i webbläsaren, och kräver
 * varken OAuth på servern eller lagrade refresh-tokens. En serverpollare kan
 * ersätta den senare utan att något annat ändras — tidslinjen ser likadan ut
 * oavsett vem som fyller den.
 */

/** Tätare än så ger inget: Spotify uppdaterar ändå inte oftare. */
const POLL_MS = 3000;

type LogRow = { at: string; text: string; warn?: boolean };

export default function SpotifyLabelPanel() {
  const [sensors, setSensors] = useState<AdminSensor[]>([]);
  const [venueId, setVenueId] = useState("");
  const [running, setRunning] = useState(false);
  const [nowPlaying, setNowPlaying] = useState<string | null>(null);
  const [posted, setPosted] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [log, setLog] = useState<LogRow[]>([]);

  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasToken = Boolean(getStoredToken());

  useEffect(() => {
    listSensors()
      .then((data) => {
        setSensors(data.items);
        const withVenue = data.items.find((s) => s.venue?.id);
        if (withVenue?.venue) setVenueId(withVenue.venue.id);
      })
      .catch((e) => setError(e?.message ?? "Could not load sensors"));
  }, []);

  const addLog = (text: string, warn = false) => {
    setLog((prev) =>
      [{ at: new Date().toLocaleTimeString(), text, warn }, ...prev].slice(0, 40)
    );
  };

  const tick = async () => {
    try {
      let token = getStoredToken();
      if (!token) return;
      if (isTokenExpired(token)) token = await refreshAccessToken();

      const playing = await getCurrentlyPlaying(token.access_token);

      if (!playing.playing || !playing.isPlaying) {
        setNowPlaying(null);
        return;
      }

      setNowPlaying(
        `${playing.artist} – ${playing.title}  (${Math.floor(
          playing.progressMs / 1000
        )}s / ${Math.floor((playing.durationMs ?? 0) / 1000)}s)`
      );

      await http.post("/admin/nowplaying", { venueId, ...playing });
      setPosted((n) => n + 1);
    } catch (e: any) {
      const message = e?.message ?? "unknown error";
      setError(message);
      addLog(message, true);
    }
  };

  const start = () => {
    if (!venueId) {
      setError("Pick a venue first.");
      return;
    }
    setError(null);
    setRunning(true);
    addLog(`Started labelling for ${venueId}`);
    // Kör direkt, vänta inte ut första intervallet.
    tick();
    timer.current = setInterval(tick, POLL_MS);
  };

  const stop = () => {
    if (timer.current) clearInterval(timer.current);
    timer.current = null;
    setRunning(false);
    addLog("Stopped labelling");
  };

  // Lämnar man sidan ska pollningen inte fortsätta i bakgrunden.
  useEffect(() => {
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, []);

  return (
    <div className="admin-panel spotify-panel">
      <p className="spotify-dim">
        Polls your Spotify account and sends what is playing to the server. The
        sensor's clips are then labelled from that instead of through AudD. This
        tab has to stay open while it runs.
      </p>

      {!hasToken && (
        <div className="spotify-row">
          <button onClick={() => startSpotifyLogin()}>Sign in with Spotify</button>
          <span className="spotify-dim">
            Needs the user-read-currently-playing scope.
          </span>
        </div>
      )}

      <div className="spotify-row">
        <label>
          Venue
          <select
            value={venueId}
            onChange={(e) => setVenueId(e.target.value)}
            disabled={running}
          >
            <option value="">Select…</option>
            {sensors
              .filter((s) => s.venue)
              .map((s) => (
                <option key={s.sensorId} value={s.venue!.id}>
                  {s.venue!.name} ({s.sensorId})
                </option>
              ))}
          </select>
        </label>

        {running ? (
          <button onClick={stop}>Stop</button>
        ) : (
          <button className="admin-btn--primary" onClick={start} disabled={!hasToken}>
            Start labelling
          </button>
        )}
      </div>

      {running && (
        <div className="spotify-status">
          <div>
            <strong>Now playing:</strong> {nowPlaying ?? "nothing (or paused)"}
          </div>
          <div className="spotify-dim">{posted} snapshots sent</div>
        </div>
      )}

      {error && <div className="admin-error">{error}</div>}

      {log.length > 0 && (
        <div className="spotify-log">
          {log.map((row, i) => (
            <div key={i} className={row.warn ? "spotify-warn" : "spotify-dim"}>
              {row.at} {row.text}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
